import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ArrowLeft, ArrowRight, FileStack } from 'lucide-react'
import type { UnassignedRequestRow } from '../api'
import { RequestObjectDetailsPopover } from './RequestObjectDetailsPopover'
import { formatDateForLocale } from '@/lib/dateUtils'
import { cn } from '@/lib/utils'
import { buildIconPath } from '@/lib/imagePaths'
import { TranslatableText } from '@/components/ui/TranslatableText'

const DND_REQUEST_ID = 'application/x-recovery-request-id'
const DND_SOURCE = 'application/x-recovery-source'
const DND_SCENARIO = 'application/x-recovery-scenario'

type ColumnId = 'unassigned' | 'inSet'

export function RequestIdsKanban(props: {
    unassignedList: UnassignedRequestRow[]
    inSetList: UnassignedRequestRow[]
    inSetLoading: boolean
    onAddToSet: (payload: { requestID: string; scenario?: number | null; objectID?: string | null; objectDescription?: string | null; forecastStartTime?: string | null }) => void
    onRemoveFromSet: (requestID: string) => void
    isAdding: boolean
    isRemoving: boolean
    /** When provided, card object-details icons use RecoverySetRecoverRequests filtered by this set + request ID */
    setId?: number | null
    /** When provided, render a "view all objects" control in the In this set panel underneath the request count */
    onViewAllObjects?: () => void
}) {
    const { t, i18n } = useTranslation()
    const locale = i18n.language || 'en-US'
    const { unassignedList, inSetList, inSetLoading, onAddToSet, onRemoveFromSet, isAdding, isRemoving, setId, onViewAllObjects } = props
    const [dragOverColumn, setDragOverColumn] = useState<ColumnId | null>(null)
    const [scenarioFilter, setScenarioFilter] = useState<string>('all')

    const hasRequestsInSet = inSetList.length > 0
    const baseScenario = hasRequestsInSet ? (inSetList[0]?.scenario ?? null) : null

    const scenarioOptions = useMemo(() => {
        const map = new Map<number, string>()
        for (const row of unassignedList) {
            if (row.scenario != null) {
                if (!map.has(row.scenario)) {
                    map.set(row.scenario, row.scenarioName ?? String(row.scenario))
                }
            }
        }
        return Array.from(map.entries())
    }, [unassignedList])

    const effectiveScenario: number | null = hasRequestsInSet
        ? (baseScenario != null ? baseScenario : null)
        : (scenarioFilter === 'all' ? null : Number.parseInt(scenarioFilter, 10))

    const filteredUnassignedList = useMemo(
        () => (effectiveScenario == null ? unassignedList : unassignedList.filter((row) => row.scenario === effectiveScenario)),
        [effectiveScenario, unassignedList]
    )

    const handleDragStart = useCallback(
        (e: React.DragEvent, requestID: string, source: ColumnId, scenario?: number | null) => {
            if (isAdding || isRemoving) return
            e.dataTransfer.setData(DND_REQUEST_ID, requestID)
            e.dataTransfer.setData(DND_SOURCE, source)
            if (scenario != null) e.dataTransfer.setData(DND_SCENARIO, String(scenario))
            e.dataTransfer.effectAllowed = 'move'
        },
        [isAdding, isRemoving]
    )

    const handleDragOver = useCallback((e: React.DragEvent, column: ColumnId) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        setDragOverColumn(column)
    }, [])

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverColumn(null)
    }, [])

    const handleDrop = useCallback(
        (e: React.DragEvent, targetColumn: ColumnId) => {
            e.preventDefault()
            setDragOverColumn(null)
            const requestID = e.dataTransfer.getData(DND_REQUEST_ID)
            const source = e.dataTransfer.getData(DND_SOURCE) as ColumnId | ''
            const scenarioRaw = e.dataTransfer.getData(DND_SCENARIO)
            const scenario = scenarioRaw !== '' ? parseInt(scenarioRaw, 10) : null
            if (!requestID || !source) return
            if (source === targetColumn) return
            if (targetColumn === 'inSet' && source === 'unassigned') {
                const row = unassignedList.find((r) => r.requestID === requestID)
                onAddToSet({
                    requestID,
                    scenario: Number.isNaN(scenario) ? null : scenario,
                    objectID: row?.objectID ?? undefined,
                    objectDescription: row?.objectDescription ?? undefined,
                    forecastStartTime: row?.forecastStartTime ?? undefined,
                })
            }
            else if (targetColumn === 'unassigned' && source === 'inSet') onRemoveFromSet(requestID)
        },
        [onAddToSet, onRemoveFromSet, unassignedList]
    )

    const requestLabelForRow = (row: UnassignedRequestRow) => {
        const obj = row.objectID ?? '—'
        const dateStr = row.forecastStartTime ? formatDateForLocale(row.forecastStartTime, locale, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : null
        return dateStr ? `${obj} – ${dateStr}` : obj
    }

    const rowContent = (row: UnassignedRequestRow) => {
        const isInProgress = row.recoveryStatus?.toLowerCase() === 'inprogress' || row.recoveryStatus?.toLowerCase() === 'in progress'
        const recoveryStatusClass = isInProgress ? 'bg-green-500/20 text-green-800 dark:text-green-200' : 'bg-muted text-muted-foreground'
        return (
            <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">{t('recoverRequests.scenario', 'Scenario')}: </span>
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                        {row.scenarioName ? <TranslatableText text={row.scenarioName} /> : '—'}
                    </span>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                    <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-sm font-medium text-foreground truncate">{row.objectID ?? '—'}</span>
                        {row.objectDescription != null && row.objectDescription !== '' && (
                            <span className="text-xs text-muted-foreground truncate"><TranslatableText text={row.objectDescription} /></span>
                        )}
                    </div>
                    {row.iconFilename && (
                        <img src={buildIconPath(row.iconFilename)} alt="" className="h-8 w-8 object-contain shrink-0" />
                    )}
                </div>
                <div className="text-xs">
                    <span className="text-muted-foreground">{t('recoverRequests.noObjects', 'No Objects')}: </span>
                    <span className="text-foreground">{row.noObjects ?? '—'}</span>
                </div>
                {(row.forecastStartTime != null || row.forecastFinishTime != null) && (
                    <div className="flex items-center gap-2 text-xs">
                        <span className="text-foreground shrink-0">
                            {row.forecastStartTime ? formatDateForLocale(row.forecastStartTime, locale, { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
                        </span>
                        <div className="flex-1 h-2 rounded-full bg-muted min-w-[60px]" aria-hidden />
                        <span className="text-foreground shrink-0">
                            {row.forecastFinishTime ? formatDateForLocale(row.forecastFinishTime, locale, { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
                        </span>
                    </div>
                )}
                {row.recoveryStatus != null && row.recoveryStatus !== '' && (
                    <span className={cn('inline-flex px-2 py-0.5 rounded text-xs font-medium', recoveryStatusClass)}>
                        {row.recoveryStatus}
                    </span>
                )}
            </div>
        )
    }

    /** Row content for "In this set" only: no forecast times, object description under object ID */
    const rowContentInSet = (row: UnassignedRequestRow) => {
        const isInProgress = row.recoveryStatus?.toLowerCase() === 'inprogress' || row.recoveryStatus?.toLowerCase() === 'in progress'
        const recoveryStatusClass = isInProgress ? 'bg-green-500/20 text-green-800 dark:text-green-200' : 'bg-muted text-muted-foreground'
        const noObjects = row.noObjects ?? row.objectCount ?? '—'
        return (
            <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">{t('recoverRequests.scenario', 'Scenario')}: </span>
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                        {row.scenarioName ? <TranslatableText text={row.scenarioName} /> : '—'}
                    </span>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                    <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-sm font-medium text-foreground truncate">{row.objectID ?? '—'}</span>
                        <span className="text-xs text-muted-foreground truncate">
                            {(row.objectDescription != null && row.objectDescription !== '') ? <TranslatableText text={row.objectDescription} /> : '—'}
                        </span>
                    </div>
                    {row.iconFilename && (
                        <img src={buildIconPath(row.iconFilename)} alt="" className="h-8 w-8 object-contain shrink-0" />
                    )}
                </div>
                <div className="text-xs">
                    <span className="text-muted-foreground">{t('recoverRequests.noObjects', 'No Objects')}: </span>
                    <span className="text-foreground">{noObjects === '—' ? '—' : noObjects}</span>
                </div>
                {(row.forecastStartTime != null || row.forecastFinishTime != null) && (
                    <div className="flex items-center gap-2 text-xs">
                        <span className="text-foreground shrink-0">
                            {row.forecastStartTime ? formatDateForLocale(row.forecastStartTime, locale, { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
                        </span>
                        <div className="flex-1 h-2 rounded-full bg-muted min-w-[60px]" aria-hidden />
                        <span className="text-foreground shrink-0">
                            {row.forecastFinishTime ? formatDateForLocale(row.forecastFinishTime, locale, { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
                        </span>
                    </div>
                )}
                {row.recoveryStatus != null && row.recoveryStatus !== '' && (
                    <span className={cn('inline-flex px-2 py-0.5 rounded text-xs font-medium', recoveryStatusClass)}>
                        {row.recoveryStatus}
                    </span>
                )}
            </div>
        )
    }

    const showScenarioFilter = !hasRequestsInSet && unassignedList.length > 0 && scenarioOptions.length > 0

    return (
        <div className="space-y-3">
            {showScenarioFilter && (
                <div className="flex justify-end">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                            {t('recoverRequests.scenarioFilter', 'Recovery scenario')}
                        </span>
                        <Select value={scenarioFilter} onValueChange={setScenarioFilter}>
                            <SelectTrigger className="h-8 w-[220px]">
                                <SelectValue placeholder={t('recoverRequests.allScenarios', 'All scenarios')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('recoverRequests.allScenarios', 'All scenarios')}</SelectItem>
                                {scenarioOptions.map(([id, name]) => (
                                    <SelectItem key={id} value={String(id)}>
                                        {name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <span className="font-medium">{t('recoverRequests.availableRequestIds', "Available Request ID's")}</span>
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-blue-500 bg-blue-50 text-xs font-semibold text-blue-700">
                            {filteredUnassignedList.length}
                        </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                        {t('recoverRequests.availableRequestIdsDescription', 'Request IDs available to add to this recovery set.')}
                    </p>
                </CardHeader>
                <CardContent
                    className={cn(
                        'space-y-2 max-h-[60vh] overflow-y-auto rounded-b-lg transition-colors',
                        dragOverColumn === 'unassigned' && 'ring-2 ring-primary/50 bg-muted/30'
                    )}
                    onDragOver={(e) => handleDragOver(e, 'unassigned')}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, 'unassigned')}
                >
                    {filteredUnassignedList.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">{t('recoverRequests.noUnassignedRequests', 'No unassigned request IDs.')}</p>
                    ) : (
                        filteredUnassignedList.map((row) => (
                            <div
                                key={row.requestID}
                                draggable={!isAdding && !isRemoving}
                                onDragStart={(e) => handleDragStart(e, row.requestID, 'unassigned', row.scenario ?? null)}
                                className="relative flex items-center justify-between gap-3 rounded-lg border bg-card p-3 hover:bg-muted/30 cursor-grab active:cursor-grabbing"
                            >
                                <div className="absolute top-2 right-2 z-10" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                                    <RequestObjectDetailsPopover requestID={row.requestID} requestLabel={requestLabelForRow(row)} />
                                </div>
                                <div className="min-w-0 flex-1 space-y-0.5 pr-8">{rowContent(row)}</div>
                                <Button size="sm" variant="outline" onClick={() => onAddToSet({ requestID: row.requestID, scenario: row.scenario ?? null, objectID: row.objectID ?? undefined, objectDescription: row.objectDescription ?? undefined, forecastStartTime: row.forecastStartTime ?? undefined })} disabled={isAdding} className="shrink-0">
                                    <ArrowRight className="h-4 w-4 mr-1" />
                                    {t('recoverRequests.addToSet', 'Add to set')}
                                </Button>
                            </div>
                        ))
                    )}
                </CardContent>
            </Card>
            <Card>
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <span className="font-medium">{t('recoverRequests.inThisSet', 'In this set')}</span>
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-blue-500 bg-blue-50 text-xs font-semibold text-blue-700">
                            {inSetLoading ? '…' : inSetList.length}
                        </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{t('recoverRequests.inThisSetDescription', 'Request IDs in this recovery set. Remove to unassign.')}</p>
                    <div className="flex items-start justify-between gap-2 mt-1.5">
                        <span className="flex-1 min-w-0" />
                        {onViewAllObjects && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0 shrink-0"
                                aria-label={t('recoverRequests.viewAllObjectsInSet', 'View all objects in this recovery set')}
                                onClick={onViewAllObjects}
                            >
                                <FileStack className="h-4 w-4 text-muted-foreground" />
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent
                    className={cn(
                        'space-y-2 max-h-[60vh] overflow-y-auto rounded-b-lg transition-colors',
                        dragOverColumn === 'inSet' && 'ring-2 ring-primary/50 bg-muted/30'
                    )}
                    onDragOver={(e) => handleDragOver(e, 'inSet')}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, 'inSet')}
                >
                    {inSetLoading ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">{t('common.loading', 'Loading…')}</p>
                    ) : inSetList.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">{t('recoverRequests.noRequestsInSet', 'No request IDs in this set.')}</p>
                    ) : (
                        inSetList.map((row) => (
                            <div
                                key={row.requestID}
                                draggable={!isAdding && !isRemoving}
                                onDragStart={(e) => handleDragStart(e, row.requestID, 'inSet')}
                                className="relative flex items-center justify-between gap-3 rounded-lg border bg-card p-3 hover:bg-muted/30 cursor-grab active:cursor-grabbing"
                            >
                                <div className="absolute top-2 right-2 z-10" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                                    <RequestObjectDetailsPopover requestID={row.requestID} requestLabel={requestLabelForRow(row)} setId={setId} />
                                </div>
                                <div className="min-w-0 flex-1 space-y-0.5 pr-8">{rowContentInSet(row)}</div>
                                <Button size="sm" variant="outline" onClick={() => onRemoveFromSet(row.requestID)} disabled={isRemoving} className="shrink-0">
                                    <ArrowLeft className="h-4 w-4 mr-1" />
                                    {t('recoverRequests.removeFromSet', 'Remove from set')}
                                </Button>
                            </div>
                        ))
                    )}
                </CardContent>
            </Card>
        </div>
        </div>
    )
}
